import { Box, HStack, ModalCloseButton, VStack } from '@chakra-ui/react';
import { Button, Input } from 'Atoms';
import { Typography } from 'Tokens';
import { DocumentIcon } from 'Tokens/Icons/Data';
import {
  GetCategoryAndStandardsQuery_,
  GetParentMaterialityAssessmentQuery_,
  GetSelectedDataPointsQuery_,
  useGetMaterialStandardsIdsQuery,
  useGetParentAssessmentMaterialMetricsQuery,
  useGetParentEsrsAssessmentIdQuery,
} from 'models';
import { JSX, useEffect, useMemo, useState } from 'react';
import { GeneralView } from './GeneralView';
import { ViewHistory } from './ViewHistory';
import { Metric, StandardsView } from './StandardsView';
import { DrView } from './DrView';
import { FrameworkRefs, Frameworks } from '../../Assessment';
import { useParams } from 'react-router-dom';
import { StandardMaterialityState } from '../DoubleMaterialityAssessment/DoubleMaterialityAssessment.d';
import { isParentMaterial } from '../MaterialityAssessment.hooks';
import { mfDisclosures, MFStandardBrowserTable } from './MFStandardBrowserTable';
import { Modal } from 'Molecules';
import { AIIcon } from 'Tokens/Icons/Function';
export type MultiFrameCategoryAndStandards = {
  mfCategoryRef: string;
  mfCategoryName: string;
  mfStandards: {
    mfStandardRef: string;
    mfStandardName: string;
    standards: {
      standardName: string;
      standardRef: string;
      materiality: StandardMaterialityState;
      frameworkName?: string;
      frameworkRef?: string;
    }[];
  }[];
}[];

type Standard =
  GetCategoryAndStandardsQuery_['MFCategory'][number]['mfStandards'][number]['mfStandardItems'][number]['standard'];

const getMaterialStandardData = (
  isCollectOnly: boolean,
  standard: Standard,
  parentMaterialStandard?: NonNullable<
    NonNullable<GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']>['parentAssessment']
  >['materialStandards'][number]
) => {
  const materialStandard = standard?.materialStandards?.[0];
  const isStandardRequiredByParent =
    parentMaterialStandard?.isMaterial && isParentMaterial(parentMaterialStandard.materialMetrics);

  const [isCollectOnlyMandatory, isCollectData] = [
    isCollectOnly && isStandardRequiredByParent,
    materialStandard?.isMaterial && isCollectOnly,
  ];

  const isStandardMandatory =
    isCollectOnly && !isStandardRequiredByParent && !standard?.isTopical
      ? false
      : !(standard?.isTopical ?? false);

  if (
    (!materialStandard && !isStandardMandatory && !isCollectOnlyMandatory) ||
    materialStandard?.isMaterial === null
  )
    return StandardMaterialityState.toAssess;
  else if (isStandardMandatory && !isCollectOnly) {
    return StandardMaterialityState.mandatory;
  } else if (isCollectOnlyMandatory) {
    return StandardMaterialityState.collectDataMandatory;
  } else if (isCollectData) {
    return StandardMaterialityState.collectData;
  } else if (!materialStandard?.isMaterial && isCollectOnly) {
    return StandardMaterialityState.doNotCollect;
  } else if (materialStandard?.isPhasedIn) {
    return StandardMaterialityState.phasedIn;
  } else if (materialStandard?.isDataGatheringOnly) {
    return StandardMaterialityState.gatherData;
  } else if (materialStandard?.isMaterial) {
    return StandardMaterialityState.material;
  }
  return StandardMaterialityState.notMaterial;
};
const getRequiredData = (
  isCollectOnly: boolean,
  allStandardsAndCategories?: GetCategoryAndStandardsQuery_['MFCategory'],
  parentMaterialStandards?: NonNullable<
    NonNullable<GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk']>['parentAssessment']
  >['materialStandards']
) => {
  const frameworkDataPointsCount: { [key: string]: [number, string] } = { all: [0, 'All'] };
  const categoryAndStandards: MultiFrameCategoryAndStandards = [];

  allStandardsAndCategories?.forEach((mfCategory) => {
    mfCategory?.mfStandards?.forEach((mfStandard) => {
      mfStandard?.mfStandardItems?.forEach((mfStandardItem) => {
        const mfCategoryExists = categoryAndStandards.find(
          (cat) => cat.mfCategoryRef === mfCategory.reference
        );
        if (mfCategoryExists) {
          const mfStandardExists = mfCategoryExists?.mfStandards?.find(
            (mfStandardInArr) => mfStandardInArr?.mfStandardRef === mfStandard?.reference
          );
          if (mfStandardExists) {
            const frameworkStandardExists = mfStandardExists?.standards?.find(
              (frameworkStandardInArr) =>
                frameworkStandardInArr?.frameworkRef ===
                mfStandardItem?.standard?.framework?.reference
            );
            if (!frameworkStandardExists) {
              const parentMaterialStandard = parentMaterialStandards?.find(
                (parentMaterialStandard) =>
                  parentMaterialStandard?.standardRef === mfStandardItem?.standard.reference
              );
              mfStandardExists?.standards?.push({
                standardName: mfStandardItem?.standard?.title,
                standardRef: mfStandardItem?.standard?.reference,
                frameworkRef: mfStandardItem?.standard?.framework?.reference,
                frameworkName: mfStandardItem?.standard?.framework?.name,
                materiality: getMaterialStandardData(
                  isCollectOnly,
                  mfStandardItem?.standard,
                  parentMaterialStandard
                ),
              });
            }
          } else {
            const parentMaterialStandard = parentMaterialStandards?.find(
              (parentMaterialStandard) =>
                parentMaterialStandard?.standardRef === mfStandardItem?.standard.reference
            );
            mfCategoryExists?.mfStandards.push({
              mfStandardRef: mfStandard.reference,
              mfStandardName: mfStandard.title,
              standards: [
                {
                  standardName: mfStandardItem?.standard?.title,
                  standardRef: mfStandardItem?.standard?.reference,
                  frameworkRef: mfStandardItem?.standard?.framework?.reference,
                  frameworkName: mfStandardItem?.standard?.framework?.name,
                  materiality: getMaterialStandardData(
                    isCollectOnly,
                    mfStandardItem?.standard,
                    parentMaterialStandard
                  ),
                },
              ],
            });
          }
        } else {
          const parentMaterialStandard = parentMaterialStandards?.find(
            (parentMaterialStandard) =>
              parentMaterialStandard?.standardRef === mfStandardItem?.standard.reference
          );
          categoryAndStandards.push({
            mfCategoryRef: mfCategory.reference,
            mfCategoryName: mfCategory.title,
            mfStandards: [
              {
                mfStandardRef: mfStandard.reference,
                mfStandardName: mfStandard.title,
                standards: [
                  {
                    standardName: mfStandardItem?.standard?.title,
                    standardRef: mfStandardItem?.standard?.reference,
                    frameworkRef: mfStandardItem?.standard?.framework?.reference,
                    frameworkName: mfStandardItem?.standard?.framework?.name,
                    materiality: getMaterialStandardData(
                      isCollectOnly,
                      mfStandardItem?.standard,
                      parentMaterialStandard
                    ),
                  },
                ],
              },
            ],
          });
        }
        mfStandardItem?.standard?.disclosureRequirementGroups?.forEach((drGroup) => {
          drGroup?.requirements?.forEach((requirement) => {
            const key = mfStandardItem?.standard?.framework?.reference;
            frameworkDataPointsCount['all'][0] +=
              requirement?.metrics_aggregate?.aggregate?.count ?? 0;
            if (key && frameworkDataPointsCount.hasOwnProperty(key)) {
              frameworkDataPointsCount[key][0] +=
                requirement?.metrics_aggregate?.aggregate?.count ?? 0;
            } else {
              frameworkDataPointsCount[key ?? ''] = [
                requirement?.metrics_aggregate?.aggregate?.count ?? 0,
                mfStandardItem?.standard?.framework?.name ?? '',
              ];
            }
          });
        });
      });
    });
  });

  return { frameworkDataPointsCount, categoryAndStandards };
};
const SideBarButton = ({
  icon,
  title,
  count,
  selected,
  onClick,
  borderBottom,
}: {
  icon: JSX.Element;
  title: string;
  count: number;
  selected: boolean;
  onClick: () => void;
  borderBottom: string;
}) => {
  return (
    <HStack
      border={'1px solid'}
      borderColor={'bg.selected'}
      width={'100%'}
      justifyContent={'space-between'}
      padding={'12px 16px'}
      cursor={'pointer'}
      bg={selected ? 'bg.selected' : 'bg.default'}
      _hover={{ background: 'bg.hover' }}
      transition="all 0.15s"
      onClick={onClick}
      borderBottom={borderBottom}
    >
      <HStack>
        {icon}
        <Typography variant="bodyStrong">{title}</Typography>
      </HStack>
      <HStack>
        <Typography variant="body" color={'text.hint'}>
          {count}
        </Typography>
      </HStack>
    </HStack>
  );
};
const SideBar = ({
  options,
}: {
  options: {
    icon: JSX.Element;
    title: string;
    count: number;
    selected: boolean;
    onClick: () => void;
  }[];
}) => (
  <VStack gap={'0px'}>
    {options.map((option, idx) => (
      <SideBarButton
        icon={option.icon}
        title={option.title}
        count={option.count}
        selected={option.selected}
        onClick={option.onClick}
        borderBottom={idx === options.length - 1 ? 'border.selected' : 'none'}
      />
    ))}
  </VStack>
);

export const DataPointsBank = ({
  onClose,
  isOpen,
  allStandardsAndCategoriesData,
  selectedDataPointsFromOtherFrameWork,
  isCollectOnly,
  parentAssessmentData,
}: {
  onClose: () => void;
  isOpen: boolean;
  allStandardsAndCategoriesData?: GetCategoryAndStandardsQuery_['MFCategory'];
  selectedDataPointsFromOtherFrameWork?: GetSelectedDataPointsQuery_['EsrsMetric'];
  isCollectOnly: boolean;
  parentAssessmentData?: GetParentMaterialityAssessmentQuery_['EsrsAssessment_by_pk'];
}) => {
  const { esrsAssessmentId } = useParams();
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [selectedStandard, setSelectedStandard] =
    useState<MultiFrameCategoryAndStandards[number]['mfStandards'][number]>();
  const [selectedView, setSelectedView] = useState('generalView');
  const [viewHistory, setViewHistory] = useState([selectedFramework]);
  const [selectedDataPoints, setSelectedDataPoints] = useState<mfDisclosures>([]);
  const [selectedDr, setSelectedDr] = useState<{
    name: string;
    ref: string;
    metrics?: Metric[];
  }>();

  const { data: parentAssessmentId } = useGetParentEsrsAssessmentIdQuery({
    variables: {
      esrsAssessmentId: esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const { data: parentMaterialMetricsData } = useGetParentAssessmentMaterialMetricsQuery({
    variables: {
      parentAssessmentId: parentAssessmentId?.esrsAssessment?.parentAssessmentId,
    },
    skip: !parentAssessmentId?.esrsAssessment?.parentAssessmentId,
  });

  const { data: materialStandards } = useGetMaterialStandardsIdsQuery({
    variables: {
      esrsAssessmentId: esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const { frameworkDataPointsCount, categoryAndStandards } = useMemo(
    () =>
      getRequiredData(
        isCollectOnly,
        allStandardsAndCategoriesData,
        parentAssessmentData?.parentAssessment?.materialStandards
      ),
    [allStandardsAndCategoriesData, isCollectOnly, parentAssessmentData]
  );

  const frameworkList = useMemo(
    () => Object.keys(frameworkDataPointsCount),
    [frameworkDataPointsCount]
  );

  const frameworkOptions = useMemo(() => {
    const options = [];
    for (const frameworkRef in frameworkDataPointsCount) {
      options.push({
        icon: <DocumentIcon />,
        title: frameworkDataPointsCount[frameworkRef][1],
        count: frameworkDataPointsCount[frameworkRef][0],
        selected: selectedFramework === frameworkRef,
        onClick: () => {
          if (selectedFramework !== frameworkRef) {
            setSelectedFramework(frameworkRef);
            setViewHistory([frameworkRef]);
            setSelectedView('generalView');
          }
        },
      });
    }
    return options;
  }, [frameworkDataPointsCount, selectedFramework]);

  useEffect(() => {
    setSelectedDataPoints(
      selectedDataPointsFromOtherFrameWork
        ?.map((met) => {
          return {
            title: met.shortTitle ?? met.title,
            type: met.metricType,
            metric: met as Metric,
            framework:
              (met.requirement.group.standard.frameworkRef as FrameworkRefs) ?? FrameworkRefs.esrs,
            reference: met.reference,
            subCategory: met.mfDisclosureRequirements?.[0]?.mfDisclosureRequirement?.title,
            description: met.description,
            materialStandardId: met.materialMetrics?.[0]?.materialStandardId,
          };
        })
        .filter((met) => met.metric.isAssessable) ?? []
    );
  }, [selectedDataPointsFromOtherFrameWork]);

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      hasFooter={selectedView !== 'generalView'}
      onConfirm={() => {
        onClose();
      }}
      hasHeader={false}
      size="xl"
      contentProps={{ w: '75%', maxW: '1800px', minW: '1200px', minHeight: '700px' }}
      bodyProps={{
        padding: '0px',
        paddingBottom: '0px',
      }}
      marginY="32px"
    >
      <HStack alignItems="flex-start" height={'100%'} spacing="0px">
        <VStack
          borderRight={'1px solid'}
          borderColor={'border.decorative'}
          height={'100%'}
          width="25%"
          gap={'0px'}
        >
          <VStack padding={'16px 16px 0px 16px'} gap={'12px'} alignSelf={'stretch'} width="100%">
            <Box alignSelf={'start'}>
              <Typography variant="h2">Data points bank</Typography>
            </Box>
            {/* <Box alignItems={'center'} gap={'8px'} alignSelf={'stretch'} width={'100%'}>
              <Input
                width={'100%'}
                placeholder="Search"
                leftIcon={<AIIcon />}
                padding={'8px 16px 8px 35px'}
                variant="ghost"
                background={'bg.interactive'}
              />
            </Box> */}
            <HStack alignSelf={'start'} width={'100%'}>
              <Button
                width={'50%'}
                selected={selectedView !== 'selected'}
                onClick={() => {
                  if (viewHistory.length === 3) {
                    setSelectedView('drView');
                  } else if (viewHistory.length === 2) {
                    setSelectedView('standardView');
                  } else {
                    setSelectedView('generalView');
                  }
                }}
              >
                All metrics
              </Button>
              <Button
                width={'50%'}
                selected={selectedView === 'selected'}
                onClick={() => setSelectedView('selected')}
              >
                Selected ({selectedDataPoints.length})
              </Button>
            </HStack>
          </VStack>
          <Box alignSelf={'start'} padding={'40px 16px 12px 16px'} width={'100%'}>
            <Typography fontSize={'12px'} fontStyle={'text.overline'} color={'text.hint'}>
              YOUR FRAMEWORKS
            </Typography>
          </Box>
          <Box width={'100%'}>
            <SideBar options={frameworkOptions} />
          </Box>
        </VStack>
        {selectedView === 'selected' && (
          <VStack
            alignSelf={'start'}
            alignItems={'start'}
            width={'100%'}
            minH={'700px'}
            padding={'32px 24px'}
            overflow={'auto'}
          >
            <HStack>
              <Typography variant="h1">Selected data points</Typography>
              <Typography fontSize={'16px'} color={'text.hint'}>
                {selectedDataPoints.length}
              </Typography>
            </HStack>
            {selectedDataPoints.length === 0 ? (
              <VStack height={'100%'} w={'100%'} justifyContent={'center'} alignItems={'center'}>
                <Typography fontSize={'14px'} color={'text.hint'}>
                  Nothing selected yet
                </Typography>
              </VStack>
            ) : (
              <MFStandardBrowserTable
                mfDisclosures={selectedDataPoints}
                parentMaterialMetrics={parentMaterialMetricsData?.esrs_MaterialMetric}
                isCollectOnly={isCollectOnly}
                hasPhasedInOption={false}
                isNotAssessedOrNotMaterial={false}
                isDrView={false}
                parentMaterialStandards={parentAssessmentData?.parentAssessment?.materialStandards}
              />
            )}
          </VStack>
        )}
        {selectedView === 'standardView' && (
          <StandardsView
            frameworkList={frameworkList}
            selectedFrameworkRef={selectedFramework}
            selectedmfStandard={selectedStandard}
            setSelectedView={setSelectedView}
            setViewHistory={setViewHistory}
            setSelectedDr={setSelectedDr}
            viewHistory={
              <ViewHistory
                viewHistory={viewHistory}
                setViewHistory={setViewHistory}
                setSelectedView={setSelectedView}
              />
            }
            parentMaterialMetrics={parentMaterialMetricsData?.esrs_MaterialMetric}
            materialStandards={materialStandards?.esrs_MaterialStandard}
            isCollectOnly={isCollectOnly}
            parentMaterialStandards={parentAssessmentData?.parentAssessment?.materialStandards}
          />
        )}
        {selectedView === 'generalView' && (
          <VStack
            alignSelf={'start'}
            padding="24px 16px"
            width={'100%'}
            gap="32px"
            alignItems={'start'}
            minH={'700px'}
            overflow={'auto'}
          >
            <Box>
              <Typography variant="h1">Explore all data points</Typography>
            </Box>
            <GeneralView
              categoryAndStandards={categoryAndStandards}
              selectedFrameworkRef={selectedFramework}
              setSelectedView={setSelectedView}
              setSelectedStandard={setSelectedStandard}
              setViewHistory={setViewHistory}
            />
          </VStack>
        )}
        {selectedView === 'drView' && (
          <DrView
            viewHistory={
              <ViewHistory
                viewHistory={viewHistory}
                setViewHistory={setViewHistory}
                setSelectedView={setSelectedView}
              />
            }
            selectedDr={selectedDr}
            materialStandards={materialStandards?.esrs_MaterialStandard}
            parentMaterialMetrics={parentMaterialMetricsData?.esrs_MaterialMetric}
            selectedFrameworkRef={selectedFramework}
            frameworkList={frameworkList}
            isCollectOnly={isCollectOnly}
            selectedmfStandard={selectedStandard}
            parentMaterialStandards={parentAssessmentData?.parentAssessment?.materialStandards}
          />
        )}
        <ModalCloseButton />
      </HStack>
    </Modal>
  );
};
